import { useState } from 'react'

import { Toasts } from '@fourthwall/components'
import type { CreateAudioPostPayload } from '@fourthwall/services/dist/MembershipsCreatorApiClient'

import { useCheckMediaCreatedMutation } from '../../../hooks/useCheckMediaCreatedMutation'
import { useImageConfirmMutation } from '../../../hooks/useImageConfirmMutation'
import { useUploadImageMutation } from '../../../hooks/useUploadImageMutation'
import { uploadMedia } from '../../../utils/uploadMedia'
import { useCreateAudioPostMutation } from './useCreateAudioPostMutation'

export const useCreateAudioPost = () => {
  const [isLoading, setLoading] = useState(false)
  const [mediaUploadProgress, setMediaUploadProgress] = useState(0)

  const { uploadImageMutation } = useUploadImageMutation()
  const { imageConfirmMutation } = useImageConfirmMutation()

  const { createAudioPostMutation } = useCreateAudioPostMutation()
  const { checkMediaCreatedMutation } = useCheckMediaCreatedMutation()

  const createAudioPost = async (
    file: File,
    thumbnail: File,
    payload: CreateAudioPostPayload,
    handleSuccess: () => void,
  ) => {
    setLoading(true)

    const { postId, audioDirectUploadData, imageDirectUploadData } =
      await createAudioPostMutation.mutateAsync([payload], {
        onError: () => setLoading(false),
      })

    await uploadImageMutation.mutateAsync([thumbnail, { ...imageDirectUploadData }], {
      onError: () => {
        setLoading(false)

        throw new Error('Memberships: create audio post upload image error')
      },
    })

    await imageConfirmMutation.mutateAsync([postId, { signedId: imageDirectUploadData.signedId }], {
      onError: () => {
        setLoading(false)

        throw new Error('Memberships: create audio post confirm image error')
      },
    })

    if (audioDirectUploadData) {
      await uploadMedia(audioDirectUploadData.uploadUrl, file, (progress: number) => {
        setMediaUploadProgress(Number(progress.toFixed()))
      }).catch(() => {
        setLoading(false)

        Toasts.notify('Media upload error. Please try again.', { type: 'error' })
      })

      checkMediaCreatedMutation.mutate(postId, {
        onSuccess: () => {
          setMediaUploadProgress(0)
          setLoading(false)
          handleSuccess()
        },
        onError: () => setLoading(false),
      })
    }
  }

  return {
    createAudioPost,
    isLoading,
    mediaUploadProgress,
  }
}
